import React, { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import { Box, Skeleton, Stack, FormControlLabel, FormGroup, Switch } from '@mui/material';
import { trackEvent } from '@surfline/web-common';

import { SWELL_SPECTRA_GRAPH_HEIGHT } from 'common/constants';

import ErrorBoundary from 'components/ErrorBoundary';
import ForecastGraphSwellSpectraReadings from 'components/ForecastGraphSwellSpectraReadings';
import ForecastGraphSlider from 'components/ForecastGraphSlider';
import GraphTimeline from 'components/GraphTimeline';
import { generateTimestamps } from 'utils/generateTimestamps';

import type {
  GraphId,
  GraphScrubbedEvent,
} from 'containers/ForecastDataVisualizer/GraphTooltips/GraphTooltipProvider/GraphTooltipProvider';
import SwellReading from 'containers/ForecastDataVisualizer/GraphTooltips/SwellTooltip/SwellReading';

import useCustomError from 'hooks/useCustomError';
import { useMaxWidthTablet, useMinWidthTabletLarge } from 'hooks/useMediaQueries';
import { BuoyTooltipData } from 'types/buoys';
import type { SwellSpectraDataHours, SwellSpectraData } from 'types/spectra';
import type { SwellsTooltipData } from 'types/graphs';
import type { Units } from 'types/units';

import { Swell } from 'types/swell';
import {
  useActiveSpectraDayIndex,
  useActiveSpectraHourIndex,
} from 'utils/globalGraphState/globalSwellSpectraState';
import ErrorMessage from '../ErrorMessage';

import ForecastGraphSwellSpectraContainer from './components/ForecastGraphSwellSpectraContainer';
import useSwellSpectra from './hooks/useSwellSpectra';
import styles from './ForecastGraphSwellSpectra.module.scss';

const GRAPH_HEIGHT_STYLE = { height: `${SWELL_SPECTRA_GRAPH_HEIGHT}px` };
const LOCAL_STORAGE_KEY = 'sl_swellSpectraGraph_toggles';

export interface Props {
  activeWave: SwellsTooltipData | BuoyTooltipData | null;
  daysToDisplay: number[];
  graphWidth: number;
  leftLastDataPointPosition?: number;
  overallMaxPeriod: number;
  renderTooltip: (xPercentage: number, graphId: GraphId) => void;
  segmentProps?: { [key: string]: any };
  trackGraphScrubbed: GraphScrubbedEvent;
  isBuoySwellSpectraGraph?: boolean;
  currentBuoyTimestamp?: number;
  startAndEndTimestamps?: {
    start: number;
    end: number;
  };
  swellSpectraData: SwellSpectraData;
  units: Units;
}

export const ForecastGraphSwellSpectraContent: React.FC<Props> = ({
  activeWave,
  daysToDisplay,
  graphWidth,
  leftLastDataPointPosition = 0,
  overallMaxPeriod,
  renderTooltip,
  segmentProps,
  trackGraphScrubbed,
  isBuoySwellSpectraGraph,
  currentBuoyTimestamp,
  startAndEndTimestamps,
  swellSpectraData,
  units,
}) => {
  const [toggles, setToggles] = useLocalStorage<Record<string, boolean>>(LOCAL_STORAGE_KEY, {});
  const isDesktop = useMinWidthTabletLarge();
  const isTablet = useMaxWidthTablet();
  const [hourIndex] = useActiveSpectraHourIndex();
  const [activeDayIndex] = useActiveSpectraDayIndex();

  const dayIndex = isBuoySwellSpectraGraph && isTablet ? 0 : activeDayIndex;

  const timelineData = useMemo(
    () =>
      generateTimestamps(
        startAndEndTimestamps?.start || 0,
        startAndEndTimestamps?.end || 0,
        swellSpectraData?.utcOffset as number,
      ),
    [startAndEndTimestamps, swellSpectraData?.utcOffset],
  );

  const {
    currentDirection,
    currentEnergy,
    currentRawEnergy,
    currentSpread,
    currentTimestamp,
    days,
    maxSwellEnergy,
    periodRange,
    rawPeriodRange,
    rawBuoyPeriodRange,
    swellTooltipContainerStyles,
    showSwellSpectraGraphContents,
    formattedBuoySpectraReadingsData,
    formattedBuoySpectraData,
    sortedTop3Swells,
    utcOffset,
    xScale,
    yScale,
  } = useSwellSpectra({
    activeWave,
    swellSpectraData,
    period: swellSpectraData?.period,
    daysToDisplay,
    dayIndex,
    hourIndex,
    graphWidth,
    fixedScale: !!toggles?.fixedScale,
    interval: isDesktop ? 1 : 2,
    isDesktop,
    isBuoySwellSpectraGraph,
    currentBuoyTimestamp,
    overallMaxPeriod,
  });

  const swellsSortedByPeriod = useMemo(
    () => sortedTop3Swells?.sort((a, b) => (a.period as number) - (b.period as number)),
    [sortedTop3Swells],
  );

  const handleToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      trackEvent('Clicked Toggle', {
        ...segmentProps,
        toggleType: 'spectra scale type',
        toggleStart: toggles?.[event.target.name] ? 'fixed' : 'dynamic',
        toggleEnd: event?.target?.checked ? 'fixed' : 'dynamic',
      });

      setToggles({
        ...toggles,
        [event.target.name]: event.target.checked,
      });
    },
    [segmentProps, setToggles, toggles],
  );

  // We set the custom width for the slider empty space when we do not have a total of 24 data points for each hour
  // This is implemented currently for "Today" on the Buoy Report page but may serve other purposes in the future.
  const sliderEmptySpaceWidth = useMemo(() => {
    const width = leftLastDataPointPosition - 10;
    const allDaysVisible = daysToDisplay.length === 3;
    const singleDayVisible = daysToDisplay.length === 1;
    const isToday = daysToDisplay[0] === 2;

    if (isBuoySwellSpectraGraph && (allDaysVisible || (singleDayVisible && isToday))) {
      return width;
    }

    return undefined;
  }, [daysToDisplay, isBuoySwellSpectraGraph, leftLastDataPointPosition]);

  const tooltips = useMemo(
    () =>
      swellsSortedByPeriod?.map((wave: Swell, index: number) =>
        wave?.period ? (
          <Box
            key={`${wave?.period}-${index + 1}`}
            className={styles.inlineSwellTooltipContainer}
            data-testid="swell-spectra-graph-tooltip"
            style={swellTooltipContainerStyles(
              wave?.period,
              swellsSortedByPeriod?.[index - 1]?.period,
            )}
          >
            <SwellReading
              activeSwell={wave?.index || 0}
              containerClassName={styles.inlineSwellTooltip}
              swell={wave as Swell}
              units={units}
            />
          </Box>
        ) : null,
      ),
    [swellTooltipContainerStyles, swellsSortedByPeriod, units],
  );

  if (swellSpectraData?.loading) {
    return (
      <Skeleton
        className={styles.loading}
        data-testid="swell-spectra-graph-loading"
        variant="rectangular"
      />
    );
  }

  return showSwellSpectraGraphContents ? (
    <>
      <FormGroup
        className={styles.formGroup}
        data-testid="swell-spectra-fixed-scale-toggle"
        onChange={handleToggleChange}
        row
      >
        <FormControlLabel
          className={styles.toggle}
          control={<Switch defaultChecked={toggles?.fixedScale} />}
          label="Fixed scale"
          name="fixedScale"
        />
      </FormGroup>
      <div style={GRAPH_HEIGHT_STYLE} data-testid="swell-spectra-graph">
        {tooltips}
        <ForecastGraphSwellSpectraContainer
          energy={currentRawEnergy}
          maxSwellEnergy={maxSwellEnergy}
          periodRange={isBuoySwellSpectraGraph ? rawBuoyPeriodRange : rawPeriodRange}
          graphXAxisTicks={isBuoySwellSpectraGraph ? swellSpectraData?.period : rawPeriodRange}
          isBuoySwellSpectraGraph={isBuoySwellSpectraGraph}
          buoySpectraTooltipYPosition={formattedBuoySpectraData}
          sortedTop3Swells={sortedTop3Swells as Array<Swell>}
          xScale={xScale}
          yScale={yScale}
        />
      </div>
      <ForecastGraphSwellSpectraReadings
        buoySpectraReadingData={formattedBuoySpectraReadingsData}
        direction={currentDirection}
        energy={currentEnergy}
        isBuoySwellSpectraGraph={isBuoySwellSpectraGraph}
        period={periodRange}
        spread={currentSpread}
        timestamp={currentTimestamp}
      />
      <ForecastGraphSlider
        customWidth={sliderEmptySpaceWidth}
        graphWidth={graphWidth}
        leftLastDataPointPosition={leftLastDataPointPosition}
        numberOfDays={daysToDisplay.length}
        renderTooltip={renderTooltip}
        trackGraphScrubbed={trackGraphScrubbed}
        utcOffset={utcOffset}
      />
      <Stack direction="row">
        {days?.map((day: SwellSpectraDataHours, index: number) => (
          <GraphTimeline
            key={day[index].timestamp}
            day={
              isBuoySwellSpectraGraph ? (timelineData?.[dayIndex] as SwellSpectraDataHours) : day
            }
            showMarkers
          />
        ))}
      </Stack>
    </>
  ) : null;
};

const ForecastGraphSwellSpectra: React.FC<Props> = ({
  activeWave,
  daysToDisplay,
  graphWidth,
  leftLastDataPointPosition,
  renderTooltip,
  swellSpectraData,
  trackGraphScrubbed,
  isBuoySwellSpectraGraph,
  currentBuoyTimestamp,
  startAndEndTimestamps,
  segmentProps,
  units,
  overallMaxPeriod,
}) => {
  const { error } = swellSpectraData;
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-graph-swell-spectra"
      render={() => (
        <Box className={styles.error} data-testid="swell-spectra-graph-error">
          <ErrorMessage Icon={ErrorIcon} message={errorMessage} />
        </Box>
      )}
    >
      <ForecastGraphSwellSpectraContent
        activeWave={activeWave}
        daysToDisplay={daysToDisplay}
        graphWidth={graphWidth}
        leftLastDataPointPosition={leftLastDataPointPosition}
        renderTooltip={renderTooltip}
        swellSpectraData={swellSpectraData}
        isBuoySwellSpectraGraph={isBuoySwellSpectraGraph}
        startAndEndTimestamps={startAndEndTimestamps}
        currentBuoyTimestamp={currentBuoyTimestamp}
        segmentProps={segmentProps}
        trackGraphScrubbed={trackGraphScrubbed}
        units={units}
        overallMaxPeriod={overallMaxPeriod}
      />
    </ErrorBoundary>
  );
};

export default ForecastGraphSwellSpectra;
